import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { CORE_API_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${CORE_API_BASE_URL}/api-keys`;

export default function useApiKeys(authToken) {
  const [apiKeys, setApiKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(err);
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // Fetch all API keys
  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(API_BASE_URL);
      setApiKeys(res.data);
    } catch (err) {
      handleAxiosErrors(err, "Fetch API Keys");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Fetch one API key by ID
  const fetchApiKeyById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`${API_BASE_URL}/${id}`);
        setSelectedKey(res.data);
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "fetch API Key details");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // Create a new API key
  const createApiKey = useCallback(
    async (data) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.post(API_BASE_URL, data);
        setApiKeys((prev) => [res.data, ...prev]);

        toast.success("API Key created successfully!", { theme: "colored" });
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "create API key");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // Update an API key
  const updateApiKey = useCallback(
    async (id, updatedData) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.put(`${API_BASE_URL}/${id}`, updatedData);

        // Update local state
        setApiKeys((prev) =>
          prev.map((key) => (key.id === id ? res.data : key)),
        );

        // If currently selected key matches, update it
        setSelectedKey((prev) => (prev && prev.id === id ? res.data : prev));

        toast.success("API Key updated successfully!", { theme: "colored" });
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "update API key");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // Delete an API key
  const deleteApiKey = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`${API_BASE_URL}/${id}`);

        // Remove deleted key from local state
        setApiKeys((prev) => prev.filter((key) => key.id !== id));
        toast.success("API Key deleted successfully!", { theme: "colored" });
      } catch (err) {
        handleAxiosErrors(err, "delete API key");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // fetch automatically on mount
  useEffect(() => {
    if (authToken) fetchApiKeys();
  }, [authToken, fetchApiKeys]);

  return {
    apiKeys,
    selectedKey,
    loading,
    error,
    fetchApiKeys,
    fetchApiKeyById,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    setSelectedKey,
  };
}
